// GET /api/transactions -- cursor pagination on (posted_at, id), per the M1
// plan §5.1 interface table ("TransactionRow, the one shape both frontend
// workstreams parse") and the 2026-09-06 UX execution plan §3/§7.3 (WS-4).
// Order is posted_at DESC, transaction_id DESC; `meta.next_cursor` is null
// on the last page. `superseded_at IS NULL` is filtered server-side by
// default -- this hook never re-requests superseded rows.
//
// No endpoint exists to call yet on a fresh backend checkout (M1 WS-D is
// concurrent with this workstream); against a backend that has not shipped
// WS-D, this simply surfaces as `isError`, which the page maps to the
// `api-unavailable` honest state -- never a fabricated fallback.
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import {
  accountScopeProofSchema,
  assertAccountScopeProof,
  createAccountScope,
} from './accountScope';
import type { ReadScope } from './useReadScope';
import type { Me } from './useMe';

export { AccountScopeUnavailableError } from './accountScope';

export const transactionAccountRefSchema = z.object({
  account_id: z.string(),
  display_name: z.string(),
  last4: z.string().nullable(),
  kind: z.string(),
});

export const merchantRefSchema = z.object({
  merchant_id: z.string(),
  display_name: z.string(),
});

export const lineItemSourceSchema = z.enum(['user', 'rule', 'ai', 'import']);

export const lineItemSchema = z.object({
  line_item_id: z.string(),
  amount: z.string(),
  category_id: z.string().nullable(),
  category_slug: z.string().nullable(),
  is_business: z.boolean(),
  source: lineItemSourceSchema,
  // NUMERIC(5,4) on the wire; may arrive as a number or a decimal string
  // depending on the JSON encoder -- accept either, never coerce null.
  confidence: z.union([z.number(), z.string()]).nullable(),
  version: z.number(),
});

export const removalReasonSchema = z.enum([
  'superseded_by_posting',
  'provider_modified',
  'provider_removed',
]);

const transactionRowFields = {
  transaction_id: z.string(),
  account: transactionAccountRefSchema,
  // SIGNED decimal string; negative = money out. Never a float.
  amount: z.string(),
  currency: z.string(),
  merchant_raw: z.string(),
  merchant: merchantRefSchema.nullable(),
  memo_raw: z.string().nullable(),
  pending: z.boolean(),
  revision: z.number(),
  // predecessor amount when revision > 1, else null -- renders
  // "bank amended $16.20 -> $17.85" without a second fetch.
  amended_from: z.string().nullable(),
  superseded_at: z.string().nullable(),
  removal_reason: removalReasonSchema.nullable(),
  // Whether a confirmed transfer/cc_payment link exists. The M1 contract
  // does not expose the link's kind on the row itself, so a live-linked row
  // renders as a generic identity transfer (§9 note in the plan; a specific
  // transfer-vs-cc_payment label needs a contract addition, tracked as a
  // follow-up, not invented here).
  has_live_link: z.boolean(),
  line_items: z.array(lineItemSchema),
};

const strictDateSchema = z.string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected strict YYYY-MM-DD')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
  }, 'expected a real calendar date');

const rfc3339Schema = z.string().datetime({ offset: true });
const postedPrecisionSchema = z.enum(['instant', 'date']);

const legacyTransactionRowSchema = z.object({
  ...transactionRowFields,
  posted_at: z.string(),
  authorized_at: z.string().nullable(),
}).strict();

const temporalTransactionRowShape = z.object({
  ...transactionRowFields,
  posted_precision: postedPrecisionSchema,
  posted_at: rfc3339Schema.nullable(),
  posted_date: strictDateSchema.nullable(),
  posted_day: strictDateSchema,
  authorized_precision: postedPrecisionSchema.nullable().optional(),
  authorized_at: rfc3339Schema.nullable(),
  authorized_date: strictDateSchema.nullable(),
}).strict();

const validateTemporalRow = (row: z.infer<typeof temporalTransactionRowShape>, context: z.RefinementCtx) => {
  if (row.posted_precision === 'instant' && (row.posted_at === null || row.posted_date !== null)) {
    context.addIssue({ code: 'custom', message: 'instant rows require posted_at and null posted_date', path: ['posted_precision'] });
  }
  if (row.posted_precision === 'date' && (row.posted_at !== null || row.posted_date === null)) {
    context.addIssue({ code: 'custom', message: 'date rows require posted_date and null posted_at', path: ['posted_precision'] });
  }
  if (row.authorized_precision === undefined || row.authorized_precision === null) {
    if (row.authorized_at !== null || row.authorized_date !== null) {
      context.addIssue({ code: 'custom', message: 'authorization fields require authorized_precision', path: ['authorized_precision'] });
    }
  } else if (row.authorized_precision === 'instant' && (row.authorized_at === null || row.authorized_date !== null)) {
    context.addIssue({ code: 'custom', message: 'instant authorization requires authorized_at and null authorized_date', path: ['authorized_precision'] });
  } else if (row.authorized_precision === 'date' && (row.authorized_at !== null || row.authorized_date === null)) {
    context.addIssue({ code: 'custom', message: 'date authorization requires authorized_date and null authorized_at', path: ['authorized_precision'] });
  }
};

export const temporalTransactionRowSchema = temporalTransactionRowShape.superRefine(validateTemporalRow);
const temporalAwareTransactionRowSchema = z.union([temporalTransactionRowSchema, legacyTransactionRowSchema]);

// Keep the established schema extension surface for the detail hook while the
// parser itself accepts both the legacy and additive temporal row unions.
export const transactionRowSchema = Object.assign(temporalAwareTransactionRowSchema, {
  extend: (shape: z.ZodRawShape) => legacyTransactionRowSchema.extend(shape),
}) as unknown as typeof legacyTransactionRowSchema;

type LegacyTransactionRow = z.infer<typeof legacyTransactionRowSchema>;
export type TemporalTransactionRow = z.infer<typeof temporalTransactionRowSchema>;
type AdditiveTemporalFields = {
  posted_precision?: z.infer<typeof postedPrecisionSchema>;
  posted_date?: string | null;
  posted_day?: string;
  authorized_precision?: z.infer<typeof postedPrecisionSchema> | null;
  authorized_date?: string | null;
};

export type TransactionAccountRef = z.infer<typeof transactionAccountRefSchema>;
export type MerchantRef = z.infer<typeof merchantRefSchema>;
export type LineItem = z.infer<typeof lineItemSchema>;
export type TransactionRow = LegacyTransactionRow & AdditiveTemporalFields;

const canonicalLineItemSchema = lineItemSchema.extend({ confidence: z.number().nullable() });
const canonicalFields = {
  line_items: z.array(canonicalLineItemSchema),
  canonical_transaction_id: z.string().uuid(),
  representative_transaction_id: z.string().uuid(),
  canonical_account_id: z.string().uuid().nullable(),
  physical_source_transaction_ids: z.array(z.string().uuid()),
  annotation_anchor_transaction_id: z.string().uuid().nullable(),
  annotation_state: z.enum(['ok', 'needs_review', 'none']),
  tombstone: z.boolean(),
};

const canonicalLegacyTransactionRowSchema = legacyTransactionRowSchema.extend(canonicalFields).strict();
const canonicalTemporalTransactionRowShape = temporalTransactionRowShape.extend(canonicalFields).strict();
const canonicalTemporalTransactionRowSchema = canonicalTemporalTransactionRowShape.superRefine(validateTemporalRow);

const temporalAwareCanonicalTransactionRowSchema = z.union([
  canonicalTemporalTransactionRowSchema,
  canonicalLegacyTransactionRowSchema,
]);
export const canonicalTransactionRowSchema = Object.assign(temporalAwareCanonicalTransactionRowSchema, {
  extend: (shape: z.ZodRawShape) => canonicalLegacyTransactionRowSchema.extend(shape),
}) as unknown as typeof canonicalLegacyTransactionRowSchema;

type LegacyCanonicalTransactionRow = z.infer<typeof canonicalLegacyTransactionRowSchema>;
export type CanonicalTransactionRow = LegacyCanonicalTransactionRow & AdditiveTemporalFields;

const finiteDecimalStringSchema = z.string().regex(/^[+-]?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?$/);

const completenessMetaSchema = z.object({
  completeness: z.enum(['complete', 'incomplete', 'initialization_required']),
  count: z.number().int().nonnegative().nullable(),
  amount: finiteDecimalStringSchema.nullable(),
  review_needed_count: z.number().int().nonnegative(),
  history_status: z.enum(['loading', 'initial_complete', 'historical_complete', 'failed']).nullable(),
  history_status_at: z.string().nullable(),
}).strict();

const temporalScopeSchema = z.object({
  timezone: z.string().min(1),
  date_from: strictDateSchema.nullable(),
  date_to: strictDateSchema.nullable(),
  calendar_basis: z.literal('saved_timezone'),
}).strict().superRefine((scope, context) => {
  if ((scope.date_from === null) !== (scope.date_to === null)) {
    context.addIssue({ code: 'custom', message: 'temporal scope date bounds must be both null or both dates', path: ['date_from'] });
  }
});

export type TemporalScope = z.infer<typeof temporalScopeSchema>;

const primaryTransactionsMetaWireSchema = z.object({
  next_cursor: z.string().nullable(),
  completeness: completenessMetaSchema,
  reconciliation_initialization_required: z.literal(true).optional(),
  uncategorized_count: z.number().int().nonnegative().optional(),
  account_scope: accountScopeProofSchema.optional(),
  temporal_scope: z.unknown().optional(),
}).strict();

const diagnosticTransactionsMetaWireSchema = z.object({
  next_cursor: z.string().nullable(),
  diagnostic_scope: z.literal(true),
  uncategorized_count: z.number().int().nonnegative().optional(),
  account_scope: accountScopeProofSchema.optional(),
  temporal_scope: z.unknown().optional(),
}).strict();

export type PrimaryTransactionsMeta = Omit<z.infer<typeof primaryTransactionsMetaWireSchema>, 'temporal_scope'> & {
  temporal_scope?: TemporalScope;
};

export type TemporalUnavailableReason =
  | 'awaiting_response'
  | 'metadata_absent'
  | 'metadata_malformed'
  | 'confirmed_timezone_unavailable'
  | 'scope_mismatch'
  | 'scope_inconsistent';

export type TemporalAvailability =
  | { availability: 'confirmed'; reason: 'saved_timezone_scope_confirmed' }
  | { availability: 'unavailable'; reason: TemporalUnavailableReason };

export class TemporalScopeError extends Error {
  readonly reason: TemporalUnavailableReason;
  readonly confirmedTimezone?: string;
  readonly responseTimezone?: string;

  constructor(reason: TemporalUnavailableReason, message: string, zones?: { confirmedTimezone?: string; responseTimezone?: string }) {
    super(message);
    this.name = 'TemporalScopeError';
    this.reason = reason;
    this.confirmedTimezone = zones?.confirmedTimezone;
    this.responseTimezone = zones?.responseTimezone;
  }
}

type TemporalParseOptions = {
  confirmedTimezone?: string;
  dateFrom?: string;
  dateTo?: string;
};

const temporalRowKeys = ['posted_precision', 'posted_date', 'posted_day', 'authorized_precision', 'authorized_date'] as const;

function hasTemporalRowFields(row: unknown): boolean {
  return typeof row === 'object' && row !== null && temporalRowKeys.some((key) => Object.prototype.hasOwnProperty.call(row, key));
}

function emitTemporalMismatch(confirmedTimezone: string, responseTimezone: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('moneyskyline:temporal-scope-mismatch', {
    detail: { confirmedTimezone, responseTimezone },
  }));
}

function throwTemporalScopeError(reason: TemporalUnavailableReason, message: string, zones?: { confirmedTimezone?: string; responseTimezone?: string }): never {
  if (zones?.confirmedTimezone && zones.responseTimezone && zones.confirmedTimezone !== zones.responseTimezone) {
    emitTemporalMismatch(zones.confirmedTimezone, zones.responseTimezone);
  }
  throw new TemporalScopeError(reason, message, zones);
}

function parseMeta<T extends { temporal_scope?: unknown }>(
  raw: unknown,
  schema: z.ZodType<T>,
): Omit<T, 'temporal_scope'> & { temporal_scope?: TemporalScope } {
  const parsed = schema.parse(raw);
  if (parsed.temporal_scope === undefined) return parsed as Omit<T, 'temporal_scope'> & { temporal_scope?: TemporalScope };
  const temporalScope = temporalScopeSchema.safeParse(parsed.temporal_scope);
  if (!temporalScope.success) {
    throwTemporalScopeError('metadata_malformed', 'temporal scope metadata is malformed');
  }
  const { temporal_scope: _ignored, ...withoutTemporalScope } = parsed;
  return { ...withoutTemporalScope, temporal_scope: temporalScope.data };
}

function requestedCalendarDate(value: string | undefined): string | null {
  if (value === undefined) return null;
  if (strictDateSchema.safeParse(value).success) return value;
  if (rfc3339Schema.safeParse(value).success) {
    const date = value.slice(0, 10);
    if (strictDateSchema.safeParse(date).success) return date;
  }
  return value;
}

function validateTemporalScope(scope: TemporalScope, options: TemporalParseOptions): TemporalAvailability {
  if (!options.confirmedTimezone) {
    throwTemporalScopeError('confirmed_timezone_unavailable', 'temporal scope requires a confirmed saved timezone');
  }
  if (scope.timezone !== options.confirmedTimezone) {
    throwTemporalScopeError('scope_mismatch', 'temporal scope timezone does not match the confirmed saved timezone', {
      confirmedTimezone: options.confirmedTimezone,
      responseTimezone: scope.timezone,
    });
  }
  const requestedFrom = requestedCalendarDate(options.dateFrom);
  const requestedTo = requestedCalendarDate(options.dateTo);
  if ((requestedFrom === null) !== (requestedTo === null) ||
      (requestedFrom !== null && !strictDateSchema.safeParse(requestedFrom).success) ||
      (requestedTo !== null && !strictDateSchema.safeParse(requestedTo).success)) {
    throwTemporalScopeError('scope_mismatch', 'requested transaction scope is not a strict date pair');
  }
  if (scope.date_from !== requestedFrom || scope.date_to !== requestedTo) {
    throwTemporalScopeError('scope_mismatch', 'temporal scope date bounds do not match the request');
  }
  return { availability: 'confirmed', reason: 'saved_timezone_scope_confirmed' };
}

function temporalAvailabilityForPage(
  meta: { temporal_scope?: TemporalScope },
  rows: unknown[],
  options: TemporalParseOptions,
): TemporalAvailability {
  const rowTemporal = rows.some(hasTemporalRowFields);
  const hasTemporal = meta.temporal_scope !== undefined || rowTemporal;
  if (!hasTemporal) return { availability: 'unavailable', reason: 'metadata_absent' };
  if (meta.temporal_scope === undefined || (rows.length > 0 && rows.some((row) => !hasTemporalRowFields(row)))) {
    throwTemporalScopeError('metadata_malformed', 'temporal scope metadata is required for temporal rows');
  }
  return validateTemporalScope(meta.temporal_scope, options);
}

const canonicalOnlyFields = [
  'canonical_transaction_id',
  'representative_transaction_id',
  'physical_source_transaction_ids',
  'annotation_anchor_transaction_id',
  'annotation_state',
  'tombstone',
] as const;

function parsePrimaryRows<T>(data: unknown[], parseRow: (row: unknown) => T): T[] {
  return data.map(parseRow);
}

function parseTransactionRow(row: unknown): TransactionRow {
  return transactionRowSchema.parse(row) as TransactionRow;
}

function parseCanonicalTransactionRow(row: unknown): CanonicalTransactionRow {
  return canonicalTransactionRowSchema.parse(row) as CanonicalTransactionRow;
}

function parseCanonicalRows(data: unknown[]): CanonicalTransactionRow[] {
  return parsePrimaryRows(data, parseCanonicalTransactionRow);
}

function parseInitializationRows(data: unknown[]): Array<CanonicalTransactionRow | TransactionRow> {
  return parsePrimaryRows(data, (row) => {
    const isCanonical = canonicalOnlyFields.some((field) =>
      Boolean(row && typeof row === 'object' && Object.prototype.hasOwnProperty.call(row, field)),
    );
    return isCanonical ? parseCanonicalTransactionRow(row) : parseTransactionRow(row);
  });
}

export type ParsedPrimaryTransactionsPage =
  | { kind: 'canonical'; data: CanonicalTransactionRow[]; meta: PrimaryTransactionsMeta; temporal?: TemporalAvailability }
  | {
      kind: 'initialization_required';
      data: Array<CanonicalTransactionRow | TransactionRow>;
      meta: PrimaryTransactionsMeta & { reconciliation_initialization_required: true };
      temporal?: TemporalAvailability;
    };

export function parsePrimaryTransactionsPage(payload: unknown, uncategorized = false, options: TemporalParseOptions = {}): ParsedPrimaryTransactionsPage {
  const parsedEnvelope = parseEnvelope(z.array(z.unknown()), payload);
  const meta = parseMeta(parsedEnvelope.meta, primaryTransactionsMetaWireSchema);
  const temporal = temporalAvailabilityForPage(meta, parsedEnvelope.data, options);
  if (meta.reconciliation_initialization_required === true) {
    if (meta.completeness.completeness !== 'initialization_required' || meta.completeness.count !== null || meta.completeness.amount !== null) {
      throw new Error('initialization-marked transactions require initialization completeness and null totals');
    }
    if (meta.uncategorized_count !== undefined) {
      throw new Error('uncategorized_count requires an explicit complete uncategorized request');
    }
    const initializationMeta: PrimaryTransactionsMeta & { reconciliation_initialization_required: true } = {
      ...meta,
      reconciliation_initialization_required: true,
    };
    try {
      return { kind: 'initialization_required', data: parseInitializationRows(parsedEnvelope.data), meta: initializationMeta, temporal };
    } catch (error) {
      if (temporal.availability === 'confirmed' || meta.temporal_scope !== undefined || parsedEnvelope.data.some(hasTemporalRowFields)) {
        throw new TemporalScopeError('metadata_malformed', 'temporal transaction row metadata is malformed', undefined);
      }
      throw error;
    }
  }
  if (meta.completeness.completeness === 'initialization_required') {
    throw new Error('initialization-required transactions must declare the initialization marker');
  }
  if (meta.completeness.completeness !== 'complete' && (meta.completeness.count !== null || meta.completeness.amount !== null)) {
    throw new Error('incomplete canonical transaction totals must be null');
  }
  if (meta.uncategorized_count !== undefined && (uncategorized !== true || meta.completeness.completeness !== 'complete')) {
    throw new Error('uncategorized_count requires an explicit complete uncategorized request');
  }
  try {
    const parsedData = meta.temporal_scope !== undefined || parsedEnvelope.data.some(hasTemporalRowFields)
      ? parsePrimaryRows(parsedEnvelope.data, parseCanonicalTransactionRow)
      : parseCanonicalRows(parsedEnvelope.data);
    return { kind: 'canonical', data: parsedData, meta, temporal };
  } catch (error) {
    if (temporal.availability === 'confirmed' || meta.temporal_scope !== undefined || parsedEnvelope.data.some(hasTemporalRowFields)) {
      throw new TemporalScopeError('metadata_malformed', 'temporal transaction row metadata is malformed', undefined);
    }
    throw error;
  }
}

export interface UseTransactionsParams {
  accountIds?: readonly string[];
  /** @deprecated Use accountIds for ordered account scope selection. */
  accountId?: string | null;
  dateFrom?: string;
  dateTo?: string;
  uncategorized?: boolean;
  scope?: ReadScope | null;
  enabled?: boolean;
}

export function useTransactions(params: UseTransactionsParams = {}) {
  const queryClient = useQueryClient();
  const { accountId, dateFrom, dateTo, uncategorized, scope, enabled = true } = params;
  const accountScope = createAccountScope(params.accountIds ?? (accountId ? [accountId] : []));
  const { accountIds } = accountScope;
  const filters = {
    accountScope: {
      mode: accountScope.mode,
      accountIds,
      fingerprint: accountScope.fingerprint,
    },
    dateFrom,
    dateTo,
    uncategorized,
  };
  const confirmedTimezone = queryClient.getQueryData<Me>(['me'])?.timezone;
  const queryKey = scope
    ? ['transactions', { ...filters, scope, timezone: confirmedTimezone }]
    : ['transactions', { ...filters, timezone: confirmedTimezone }];
  const query = useInfiniteQuery({
    queryKey,
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const requestParams = new URLSearchParams();
      accountIds.forEach((selectedAccountId) => requestParams.append('account_id', selectedAccountId));
      if (pageParam !== undefined) requestParams.set('cursor', pageParam);
      if (dateFrom !== undefined) requestParams.set('date_from', dateFrom);
      if (dateTo !== undefined) requestParams.set('date_to', dateTo);
      if (uncategorized !== undefined) requestParams.set('uncategorized', String(uncategorized));
      if (scope) requestParams.set('scope', scope);
      const response = await client.get('/api/transactions', { params: requestParams });
      if (scope) {
        const parsedEnvelope = parseEnvelope(z.array(z.unknown()), response.data);
        const meta = parseMeta(parsedEnvelope.meta, diagnosticTransactionsMetaWireSchema);
        assertAccountScopeProof(meta, accountIds);
        const temporal = temporalAvailabilityForPage(meta, parsedEnvelope.data, { confirmedTimezone, dateFrom, dateTo });
        if (meta.uncategorized_count !== undefined && uncategorized !== true) {
          throw new Error('diagnostic uncategorized_count requires an explicit uncategorized request');
        }
        try {
          return { data: parsedEnvelope.data.map(parseTransactionRow), meta };
        } catch (error) {
          if (temporal.availability === 'confirmed' || meta.temporal_scope !== undefined || parsedEnvelope.data.some(hasTemporalRowFields)) {
            throw new TemporalScopeError('metadata_malformed', 'temporal transaction row metadata is malformed', undefined);
          }
          throw error;
        }
      }
      const parsed = parsePrimaryTransactionsPage(response.data, uncategorized, { confirmedTimezone, dateFrom, dateTo });
      assertAccountScopeProof(parsed.meta, accountIds);
      return { data: parsed.data, meta: parsed.meta };
    },
    getNextPageParam: (lastPage) => lastPage.meta?.next_cursor ?? undefined,
    select: (data) => {
      const temporalPages = data.pages.map((page) => temporalAvailabilityForPage(page.meta, page.data, { confirmedTimezone, dateFrom, dateTo }));
      const firstTemporal = temporalPages[0];
      if (firstTemporal && temporalPages.some((value) => JSON.stringify(value) !== JSON.stringify(firstTemporal))) {
        throw new TemporalScopeError('scope_inconsistent', 'transaction pages disagree on temporal scope');
      }
      return data;
    },
  });
  const temporal = query.error instanceof TemporalScopeError
    ? { availability: 'unavailable' as const, reason: query.error.reason }
    : query.data?.pages[0]
      ? temporalAvailabilityForPage(query.data.pages[0].meta, query.data.pages[0].data, { confirmedTimezone, dateFrom, dateTo })
      : { availability: 'unavailable' as const, reason: 'awaiting_response' as const };
  return { ...query, temporal };
}
